import { useState, useRef, useEffect } from 'react';
import { BaseControl } from '.';
import useSettings from '../../../../hooks/Settings';
import LanguageIcon from '@mui/icons-material/Language';
import Tooltip from '../../../common/Tooltip';

/**
 * @typedef {object} ColorControlOptions
 * @property {boolean} [useHousestyles] - Whether to only show housestyle colors in the options
 * @property {string} [noneLabel] - Label for the "no color" option in the housestyle picker
 * @property {string} [backgroundColor] - Background color
 * @property {string} [color] - Text color
 */

/**
 * @extends {BaseControl<string, ColorControlOptions>}
 */
export class ColorControl extends BaseControl {

    /** @type {BaseControl<string>['render']} */
    render(value, onChange) {
        return (
            <ColorControlRenderer
                control={this}
                value={value}
                onChange={onChange}
            />
        );
    }
}

/**
 * @param {object} props
 * @param {ColorControl} props.control
 * @param {string} props.value - Current value of the control
 * @param {(newValue: string) => void} props.onChange
 * @returns
 */
const ColorControlRenderer = ({ control, value, onChange }) => {
    const [useHousestyles, setUseHousestyles] = useState(
        control.options?.useHousestyles ?? false
    );

    return (
        <label className="color-control">
            {control.label}
            <div className="color-control-pickers">
                {(
                    <button
                        type="button"
                        className={`color-control-housestyle-toggle${
                            useHousestyles
                                ? ' color-control-housestyle-toggle--active'
                                : ''
                        }`}
                        onClick={() => setUseHousestyles((v) => !v)}
                        title="Huisstijl kleuren"
                    >
                        {/* <Tooltip content={'Huisstijl kleuren'} top='-60px' width={'100%'}> */}
                            <LanguageIcon fontSize="inherit" />
                        {/* </Tooltip> */}
                    </button>
                )}
                {useHousestyles ? (
                    <ColorPickerHousestyle
                        value={value}
                        onChange={v => onChange(v)}
                        noneLabel={control.options?.noneLabel}
                    />
                ) : (
                    <ColorPickerNative
                        label={control.label}
                        value={value}
                        onChange={onChange}
                    />
                )}
            </div>
        </label>
    );
};

/**
 * Trigger button shared by all color picker variants.
 * @param {object} props
 * @param {string} [props.value] - Current color value (hex string)
 * @param {() => void} props.onClick
 * @param {boolean} [props.open] - Controls aria-expanded
 */
const ColorPickerTrigger = ({ value, onClick, open }) => (
    <button
        type="button"
        className="color-picker-dropdown-trigger"
        onClick={onClick}
        aria-haspopup="listbox"
        aria-expanded={open ?? false}
    >
        <span
            className={`color-picker-dropdown-preview${
                !value ? ' color-picker-swatch-none' : ''
            }`}
            style={value ? { backgroundColor: value } : {}}
        />
        <span className="color-picker-dropdown-chevron">
            ▾
        </span>
    </button>
);

/**
 * Shared dropdown shell used by both color picker variants.
 * Handles open/close state and click-outside dismissal.
 * @param {object} props
 * @param {string} [props.value] - Current color value (hex string)
 * @param {boolean} props.open - Whether the panel is open
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setOpen
 * @param {React.RefObject<HTMLDivElement>} props.containerRef
 * @param {string} [props.panelClassName] - Extra class for the panel
 * @param {React.ReactNode} props.children - Panel contents
 */
const ColorPickerDropdown = ({
    value,
    open,
    setOpen,
    containerRef,
    panelClassName,
    children,
}) => {
    /* Close when clicking outside */
    useEffect(() => {
        if (!open) return;

        /** @param {any} e */
        const handleClickOutside = (e) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, containerRef, setOpen]);

    return (
        <div
            className="color-picker-dropdown-container"
            ref={containerRef}
        >
            <ColorPickerTrigger
                value={value}
                onClick={() => setOpen((open) => !open)}
                open={open}
            />

            {open && (
                <div className={`color-picker-dropdown-panel${panelClassName ? ` ${panelClassName}` : ''}`}>
                    {children}
                </div>
            )}
        </div>
    );
};

/**
 * Native `<input type="color">` — clicking the trigger opens the
 * browser's built-in color dialog directly.
 * @param {object} props
 * @param {string} props.label
 * @param {string} [props.value]
 * @param {(value: string) => void} props.onChange
 */
const ColorPickerNative = ({ label, value, onChange }) => {
    
    /** @type {React.RefObject<HTMLInputElement>} */
    const inputRef = useRef(null);

    return (
        <div className="color-picker-dropdown-container">
            <ColorPickerTrigger
                value={value}
                onClick={() => inputRef.current?.click()}
            />
            <input
                ref={inputRef}
                type="color"
                name={label}
                value={value || '#ffffff'}
                onChange={(e) => onChange(e.target.value)}
                aria-label={label}
                style={{ position: 'absolute', opacity: 0,
                    width: 0, height: 0, pointerEvents: 'none' }}
            />
        </div>
    );
};

/**
 * Custom color picker showing a fixed palette based on the site's housestyle colors.
 * @param {object} props
 * @param {string} [props.value]
 * @param {(value: string) => void} props.onChange
 * @param {string} [props.noneLabel] - Label for the "no color" option
 */
const ColorPickerHousestyle = ({ value, onChange, noneLabel = 'Geen achtergrond' }) => {
    const { PrimaryColor, SecundairyColor, TertairyColor } = useSettings();
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const colorOptions = {
        'Primaire kleur': PrimaryColor,
        'Secundaire kleur': SecundairyColor,
        'Tertiaire kleur': TertairyColor,
        [noneLabel]: '',
    };

    return (
        <ColorPickerDropdown
            value={value}
            open={open}
            setOpen={setOpen}
            containerRef={containerRef}
        >
            {Object.entries(colorOptions).map(([name, color]) => (
                <label
                    key={name}
                    className="color-picker-select-color-container"
                    title={name}
                >
                    <input
                        type="radio"
                        name="color-picker-housestyle"
                        value={color}
                        checked={value === color}
                        onChange={(e) => {
                            onChange(e.target.value);
                            setOpen(false);
                        }}
                    />
                    <div
                        className={`color-picker-color-container${
                            !color ? ' color-picker-swatch-none' : ''
                        }`}
                        style={color ? { backgroundColor: color } : {}}
                    />
                </label>
            ))}
        </ColorPickerDropdown>
    );
};
